import { useEffect, useState, useCallback } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import api from "@/lib/api";
import { Seo } from "@/components/Seo";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { LogOut, Plus, Pencil, Trash2, LayoutDashboard, MessageSquare, Users, HelpCircle, FileText, Video, Images, BookOpen } from "lucide-react";

const TYPES = {
  faqs: { label: "FAQs", fields: [ { name: "question", label: "Question" }, { name: "answer", label: "Answer", type: "textarea" }, { name: "category", label: "Category" } ] },
  articles: { label: "Articles", fields: [ { name: "title", label: "Title" }, { name: "slug", label: "Slug" }, { name: "category", label: "Category" }, { name: "excerpt", label: "Excerpt", type: "textarea" }, { name: "cover_image", label: "Cover image URL" }, { name: "read_minutes", label: "Read minutes", type: "number" }, { name: "tags", label: "Tags (comma separated)", type: "tags" }, { name: "content", label: "Content (HTML)", type: "textarea" }, { name: "status", label: "Status (published/draft)" } ] },
  videos: { label: "Videos", fields: [ { name: "title", label: "Title" }, { name: "category", label: "Category" }, { name: "youtube_url", label: "YouTube URL" }, { name: "description", label: "Description", type: "textarea" } ] },
  gallery: { label: "Gallery", fields: [ { name: "title", label: "Title" }, { name: "category", label: "Category" }, { name: "before_image", label: "Before image URL" }, { name: "after_image", label: "After image URL" }, { name: "caption", label: "Caption" } ] },
  stories: { label: "Patient Stories", fields: [ { name: "title", label: "Title" }, { name: "summary", label: "Summary", type: "textarea" }, { name: "image", label: "Image URL" }, { name: "timeline", label: "Timeline", type: "timeline" } ] },
};

const STAGES = ["Problem", "Diagnosis", "Treatment", "Outcome"];

function Login({ onLogin }) {
  const { register, handleSubmit } = useForm();
  const [loading, setLoading] = useState(false);
  const submit = async (v) => {
    setLoading(true);
    try {
      const r = await api.post("/admin/login", v);
      localStorage.setItem("cdt_admin_token", r.data.token);
      onLogin();
    } catch (e) { toast.error("Invalid credentials"); }
    finally { setLoading(false); }
  };
  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4">
      <div className="w-full max-w-sm rounded-[var(--cdt-radius-lg)] bg-white border border-[color:var(--cdt-border)] shadow-[var(--cdt-shadow-md)] p-8">
        <h1 className="font-display text-2xl text-[var(--cdt-text)]">CDT Admin</h1>
        <p className="text-sm text-[var(--cdt-muted)] mt-1">Sign in to manage content</p>
        <form onSubmit={handleSubmit(submit)} className="mt-6 space-y-4">
          <input {...register("email")} data-testid="admin-email-input" placeholder="Email" className="w-full h-11 px-3 rounded-[var(--cdt-radius-sm)] border border-[color:var(--cdt-border)] text-sm" />
          <input {...register("password")} type="password" data-testid="admin-password-input" placeholder="Password" className="w-full h-11 px-3 rounded-[var(--cdt-radius-sm)] border border-[color:var(--cdt-border)] text-sm" />
          <button disabled={loading} data-testid="admin-login-button" className="w-full h-11 rounded-[var(--cdt-radius-sm)] bg-[var(--cdt-primary)] text-white font-semibold disabled:opacity-60">{loading ? "Signing in..." : "Sign In"}</button>
        </form>
      </div>
    </div>
  );
}

function ItemForm({ type, item, onSaved }) {
  const cfg = TYPES[type];
  const [values, setValues] = useState(() => {
    const base = {};
    cfg.fields.forEach((f) => {
      if (f.type === "tags") base[f.name] = (item?.[f.name] || []).join(", ");
      else if (f.type === "timeline") base[f.name] = item?.timeline || STAGES.map((s) => ({ stage: s, detail: "" }));
      else base[f.name] = item?.[f.name] ?? "";
    });
    return base;
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    const payload = { ...values };
    cfg.fields.forEach((f) => {
      if (f.type === "tags") payload[f.name] = String(values[f.name]).split(",").map((t) => t.trim()).filter(Boolean);
      if (f.type === "number") payload[f.name] = Number(values[f.name]) || 0;
    });
    if (type === "articles" && !payload.status) payload.status = "published";
    try {
      if (item?.id) await api.put(`/admin/${type}/${item.id}`, payload);
      else await api.post(`/admin/${type}`, payload);
      toast.success("Saved");
      onSaved();
    } catch (e) { toast.error("Save failed. Check required fields."); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-3 max-h-[65vh] overflow-y-auto pr-1">
      {cfg.fields.map((f) => (
        <div key={f.name}>
          <label className="text-xs font-medium text-[var(--cdt-text)]">{f.label}</label>
          {f.type === "textarea" ? (
            <textarea value={values[f.name]} onChange={(e) => setValues({ ...values, [f.name]: e.target.value })} data-testid={`admin-field-${f.name}`} rows={f.name === "content" ? 6 : 3} className="mt-1 w-full px-3 py-2 rounded-lg border border-[color:var(--cdt-border)] text-sm" />
          ) : f.type === "timeline" ? (
            <div className="mt-1 space-y-2">
              {values.timeline.map((t, i) => (
                <div key={i} className="flex gap-2 items-start">
                  <span className="text-xs w-20 pt-2 text-[var(--cdt-muted)]">{t.stage}</span>
                  <textarea value={t.detail} onChange={(e) => { const nt = [...values.timeline]; nt[i] = { ...nt[i], detail: e.target.value }; setValues({ ...values, timeline: nt }); }} rows={2} className="flex-1 px-3 py-2 rounded-lg border border-[color:var(--cdt-border)] text-sm" />
                </div>
              ))}
            </div>
          ) : (
            <input type={f.type === "number" ? "number" : "text"} value={values[f.name]} onChange={(e) => setValues({ ...values, [f.name]: e.target.value })} data-testid={`admin-field-${f.name}`} className="mt-1 w-full h-10 px-3 rounded-lg border border-[color:var(--cdt-border)] text-sm" />
          )}
        </div>
      ))}
      <button onClick={save} disabled={saving} data-testid="admin-save-item" className="w-full h-11 rounded-[var(--cdt-radius-sm)] bg-[var(--cdt-primary)] text-white font-semibold disabled:opacity-60">{saving ? "Saving..." : "Save"}</button>
    </div>
  );
}

function ContentManager({ type }) {
  const cfg = TYPES[type];
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => { api.get(`/admin/${type}`).then((r) => setItems(r.data)).catch(() => {}); }, [type]);
  useEffect(() => { load(); }, [load]);

  const remove = async (id) => { try { await api.delete(`/admin/${type}/${id}`); toast.success("Deleted"); load(); } catch { toast.error("Delete failed"); } };
  const titleField = cfg.fields[0].name;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-xl text-[var(--cdt-text)]">{cfg.label} <span className="text-sm text-[var(--cdt-muted)]">({items.length})</span></h2>
        <Dialog open={open && !editing} onOpenChange={(o) => { setOpen(o); if (!o) setEditing(null); }}>
          <DialogTrigger asChild><button onClick={() => { setEditing(null); setOpen(true); }} data-testid={`admin-add-${type}`} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-[var(--cdt-primary)] text-white text-sm font-medium"><Plus className="h-4 w-4" /> Add</button></DialogTrigger>
          <DialogContent className="max-w-lg"><DialogHeader><DialogTitle>Add {cfg.label}</DialogTitle></DialogHeader><ItemForm type={type} onSaved={() => { setOpen(false); load(); }} /></DialogContent>
        </Dialog>
      </div>
      <div className="space-y-2">
        {items.map((it) => (
          <div key={it.id} className="flex items-center justify-between gap-3 rounded-lg bg-white border border-[color:var(--cdt-border)] px-4 py-3">
            <span className="text-sm text-[var(--cdt-text)] truncate">{it[titleField]}</span>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Dialog open={editing?.id === it.id} onOpenChange={(o) => setEditing(o ? it : null)}>
                <DialogTrigger asChild><button data-testid={`admin-edit-${it.id}`} className="h-8 w-8 rounded-md border border-[color:var(--cdt-border)] flex items-center justify-center hover:bg-[var(--cdt-surface-2)]"><Pencil className="h-3.5 w-3.5 text-[var(--cdt-muted)]" /></button></DialogTrigger>
                <DialogContent className="max-w-lg"><DialogHeader><DialogTitle>Edit {cfg.label}</DialogTitle></DialogHeader><ItemForm type={type} item={it} onSaved={() => { setEditing(null); load(); }} /></DialogContent>
              </Dialog>
              <AlertDialog>
                <AlertDialogTrigger asChild><button data-testid={`admin-delete-${it.id}`} className="h-8 w-8 rounded-md border border-[color:var(--cdt-border)] flex items-center justify-center hover:bg-red-50"><Trash2 className="h-3.5 w-3.5 text-red-500" /></button></AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader><AlertDialogTitle>Delete this item?</AlertDialogTitle><AlertDialogDescription>This action cannot be undone.</AlertDialogDescription></AlertDialogHeader>
                  <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => remove(it.id)} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction></AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-sm text-[var(--cdt-muted)] py-8 text-center">No items yet.</p>}
      </div>
    </div>
  );
}

function LeadsTable({ endpoint, columns }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get(`/admin/${endpoint}`).then((r) => setRows(r.data)).catch(() => {}); }, [endpoint]);
  return (
    <div className="overflow-x-auto rounded-lg border border-[color:var(--cdt-border)] bg-white">
      <table className="w-full text-sm">
        <thead className="bg-[var(--cdt-surface-2)]"><tr>{columns.map((c) => <th key={c.key} className="text-left px-4 py-2.5 font-medium text-[var(--cdt-muted)] whitespace-nowrap">{c.label}</th>)}</tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-[color:var(--cdt-border)]">{columns.map((c) => <td key={c.key} className="px-4 py-2.5 text-[var(--cdt-text)]">{c.key === "created_at" ? new Date(r[c.key]).toLocaleString() : (r[c.key] || "-")}</td>)}</tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={columns.length} className="px-4 py-8 text-center text-[var(--cdt-muted)]">No records yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);

  const verify = useCallback(() => {
    const token = localStorage.getItem("cdt_admin_token");
    if (!token) { setAuthed(false); setChecking(false); return; }
    api.get("/admin/me").then(() => setAuthed(true)).catch(() => { localStorage.removeItem("cdt_admin_token"); setAuthed(false); }).finally(() => setChecking(false));
  }, []);
  useEffect(() => { verify(); }, [verify]);
  useEffect(() => { if (authed) api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {}); }, [authed, tab]);

  const logout = () => { localStorage.removeItem("cdt_admin_token"); setAuthed(false); };

  if (checking) return <div className="min-h-[60vh] flex items-center justify-center text-[var(--cdt-muted)]">Loading...</div>;
  if (!authed) return (<><Seo title="Admin" path="/admin" /><Login onLogin={verify} /></>);

  const nav = [
    { key: "overview", label: "Overview", Icon: LayoutDashboard },
    { key: "appointments", label: "Appointments", Icon: MessageSquare },
    { key: "contacts", label: "Contacts", Icon: MessageSquare },
    { key: "leads", label: "Leads", Icon: Users },
    { key: "faqs", label: "FAQs", Icon: HelpCircle },
    { key: "articles", label: "Articles", Icon: FileText },
    { key: "videos", label: "Videos", Icon: Video },
    { key: "gallery", label: "Gallery", Icon: Images },
    { key: "stories", label: "Stories", Icon: BookOpen },
  ];

  return (
    <>
      <Seo title="Admin Dashboard" path="/admin" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-dashboard">
        <div className="flex items-center justify-between mb-6">
          <h1 className="font-display text-2xl text-[var(--cdt-text)]">Content Dashboard</h1>
          <button onClick={logout} data-testid="admin-logout-button" className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-[color:var(--cdt-border)] text-sm text-[var(--cdt-text)]"><LogOut className="h-4 w-4" /> Logout</button>
        </div>
        <div className="grid lg:grid-cols-[220px_1fr] gap-6">
          <nav className="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible">
            {nav.map((n) => (
              <button key={n.key} onClick={() => setTab(n.key)} data-testid={`admin-tab-${n.key}`} className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm whitespace-nowrap transition-colors ${tab === n.key ? "bg-[var(--cdt-primary)] text-white" : "text-[var(--cdt-text)] hover:bg-[var(--cdt-surface-2)]"}`}>
                <n.Icon className="h-4 w-4" /> {n.label}
              </button>
            ))}
          </nav>
          <div className="min-w-0">
            {tab === "overview" && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {stats && Object.entries(stats).map(([k, v]) => (
                  <div key={k} className="rounded-[var(--cdt-radius-md)] bg-white border border-[color:var(--cdt-border)] p-5">
                    <p className="font-display text-3xl text-[var(--cdt-primary)]">{v}</p>
                    <p className="text-xs uppercase tracking-wide text-[var(--cdt-muted)] mt-1 capitalize">{k}</p>
                  </div>
                ))}
              </div>
            )}
            {tab === "appointments" && <LeadsTable endpoint="appointments" columns={[{ key: "created_at", label: "Date" }, { key: "name", label: "Name" }, { key: "phone", label: "Phone" }, { key: "email", label: "Email" }, { key: "service", label: "Service" }, { key: "patient_type", label: "Type" }, { key: "message", label: "Message" }]} />}
            {tab === "contacts" && <LeadsTable endpoint="contacts" columns={[{ key: "created_at", label: "Date" }, { key: "name", label: "Name" }, { key: "email", label: "Email" }, { key: "phone", label: "Phone" }, { key: "subject", label: "Subject" }, { key: "message", label: "Message" }]} />}
            {tab === "leads" && <LeadsTable endpoint="leads" columns={[{ key: "created_at", label: "Date" }, { key: "name", label: "Name" }, { key: "phone", label: "Phone" }, { key: "source", label: "Source" }, { key: "message", label: "Message" }]} />}
            {["faqs", "articles", "videos", "gallery", "stories"].includes(tab) && <ContentManager type={tab} />}
          </div>
        </div>
      </div>
    </>
  );
}
